// Saved Jobs Module - API service for job bookmarking functionality

import { BaseModule } from './baseModule';
import type { AxiosInstance } from 'axios';
import type { ErrorHandler } from './errorHandler';

export interface SavedJob {
  id: string;
  job_id: string;
  saved_at: string;
  title: string | null;
  company: string | null;
  location: string | null;
  job_type: string | null;
  salary_min: number | null;
  salary_max: number | null;
  status: string | null;
  company_logo_url: string | null;
}

export interface SaveJobResponse {
  success: boolean;
  message: string;
  saved: boolean;
}

export class SavedJobsModule extends BaseModule {
  constructor(client: AxiosInstance, errorHandler: ErrorHandler) {
    super(client, errorHandler);
  }

  /**
   * Get all saved jobs for the current user
   */
  async getSavedJobs(): Promise<SavedJob[]> {
    return this.get<SavedJob[]>('/api/saved-jobs');
  }

  /**
   * Save a job
   */
  async saveJob(jobId: string): Promise<SaveJobResponse> {
    return this.post<SaveJobResponse>('/api/saved-jobs', {
      job_id: jobId,
    });
  }

  /**
   * Unsave/remove a saved job
   */
  async unsaveJob(jobId: string): Promise<SaveJobResponse> {
    return this.delete<SaveJobResponse>(`/api/saved-jobs/${jobId}`);
  }

  /**
   * Check if a job is saved
   */
  async isJobSaved(jobId: string): Promise<boolean> {
    const response = await this.get<SaveJobResponse>(`/api/saved-jobs/${jobId}`);
    return response.saved;
  }

  /**
   * Toggle save status for a job
   */
  async toggleSaveJob(jobId: string, currentlySaved: boolean): Promise<SaveJobResponse> {
    if (currentlySaved) {
      return this.unsaveJob(jobId);
    } else {
      return this.saveJob(jobId);
    }
  }
}
